"use client";

import { useState, useRef, useCallback, useEffect, useMemo } from "react";

export interface UseArTableSelectionConfig<TRow> {
  data: TRow[];
  rowKey: (row: TRow) => string;
}

export interface ArTableSelection {
  selectedIds: Set<string>;
  toggle: (id: string) => void;
  selectAll: (ids: string[]) => void;
  clearSelection: () => void;
  isSelected: (id: string) => boolean;
  allSelected: boolean;
  someSelected: boolean;
  handleCheckboxClick: (id: string, idx: number, e: React.MouseEvent) => void;
  handleRowShiftClick: (idx: number) => boolean;
}

export function useArTableSelection<TRow>(
  config: UseArTableSelectionConfig<TRow>,
): ArTableSelection {
  const { data, rowKey } = config;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const lastClickedIdx = useRef<number | null>(null);

  const toggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback((ids: string[]) => {
    setSelectedIds(new Set(ids));
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const isSelected = useCallback(
    (id: string) => selectedIds.has(id),
    [selectedIds],
  );

  const handleShiftSelect = useCallback(
    (idx: number): boolean => {
      if (lastClickedIdx.current === null) return false;
      const start = Math.min(lastClickedIdx.current, idx);
      const end = Math.max(lastClickedIdx.current, idx);
      const rangeIds = data.slice(start, end + 1).map((r) => rowKey(r));
      setSelectedIds((prev) => {
        const merged = new Set(prev);
        for (const id of rangeIds) merged.add(id);
        return merged;
      });
      lastClickedIdx.current = idx;
      return true;
    },
    [data, rowKey],
  );

  const handleCheckboxClick = useCallback(
    (id: string, idx: number, e: React.MouseEvent) => {
      if (e.shiftKey && handleShiftSelect(idx)) return;
      toggle(id);
      lastClickedIdx.current = idx;
    },
    [toggle, handleShiftSelect],
  );

  const handleRowShiftClick = useCallback(
    (idx: number): boolean => {
      if (lastClickedIdx.current === null) return false;
      return handleShiftSelect(idx);
    },
    [handleShiftSelect],
  );

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && selectedIds.size > 0) {
        clearSelection();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [selectedIds.size, clearSelection]);

  const allSelected = useMemo(
    () => data.length > 0 && data.every((r) => selectedIds.has(rowKey(r))),
    [data, selectedIds, rowKey],
  );

  const someSelected = selectedIds.size > 0;

  return {
    selectedIds,
    toggle,
    selectAll,
    clearSelection,
    isSelected,
    allSelected,
    someSelected,
    handleCheckboxClick,
    handleRowShiftClick,
  };
}
